import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { GuidePage, type GuideLead } from "@/components/guide-page";
import { SeasonChart } from "@/components/season-chart";
import { RangeTable } from "@/components/range-table";
import { STATES } from "@/lib/states";
import type { Guide } from "@/lib/guides";
import { NotFoundPage } from "@/components/not-found";
import { faqJsonLd } from "@/components/faq";
import { breadcrumbJsonLd, datasetJsonLd } from "@/lib/jsonld";
import { CDC_STATUS_SOURCE } from "@/lib/county-status-text";
import { articleJsonLd } from "@/lib/jsonld";
import { absUrl, ogImageUrl } from "@/lib/site";
import { pageTitle } from "@/lib/seo";

export const Route = createFileRoute("/$guide")({
  // The content module is imported dynamically so it lands in this route's own chunk
  // instead of the shared entry chunk that every page downloads. routeTree.gen.ts
  // imports each route's loader and head eagerly, so a top-level import here would
  // put this file's prose on the home page's critical path.
  loader: async ({ params }): Promise<{ guide: Guide }> => {
    const { getGuide } = await import("@/lib/guides");
    const guide = getGuide(params.guide);
    if (!guide) {
      // "/tick-bite", "/identify", "/maps", "/georgia": send a likely guess to the page
      // it meant before falling back to the not-found page.
      const { resolveRedirect } = await import("@/lib/redirects");
      const target = resolveRedirect(`/${params.guide}`);
      if (target) throw redirect({ href: target, statusCode: 301 });
      throw notFound();
    }
    return { guide };
  },
  head: ({ loaderData, params }) => {
    const g = loaderData?.guide;
    if (!g) return {};
    const path = `/${params.guide}`;
    const ogImage = ogImageUrl({
      title: g.h1,
      subtitle: "Lone Star tick & alpha-gal awareness",
      kind: "guide",
    });
    return {
      meta: [
        { title: pageTitle(g.metaTitle) },
        { name: "description", content: g.metaDescription },
        { name: "keywords", content: g.keywords.join(", ") },
        { property: "og:title", content: g.metaTitle },
        { property: "og:description", content: g.metaDescription },
        { property: "og:url", content: absUrl(path) },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { name: "twitter:title", content: g.metaTitle },
        { name: "twitter:description", content: g.metaDescription },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: absUrl(path) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            articleJsonLd({
              headline: g.h1,
              description: g.metaDescription,
              path,
              datePublished: g.lastReviewed,
              keywords: g.keywords,
              image: ogImage,
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(g.faq)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Guides", path: "/resources" },
              { name: g.h1, path },
            ]),
          ),
        },
        ...(g.slug === "where-do-lone-star-ticks-live"
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(
                  datasetJsonLd({
                    name: "Lone Star tick county records, counted by state",
                    description: `For each US state, how many counties CDC lists as having established Lone Star tick (Amblyomma americanum) populations, how many have reports of the tick, and how many have no records. Counted from CDC's ${CDC_STATUS_SOURCE.year} county surveillance data.`,
                    path,
                    isBasedOn: CDC_STATUS_SOURCE.url,
                    temporalCoverage: String(CDC_STATUS_SOURCE.year),
                    variableMeasured: [
                      "Counties where CDC lists the Lone Star tick as established",
                      "Counties where CDC has reports of the Lone Star tick",
                      "Counties in the state",
                    ],
                    keywords: [
                      "Lone Star tick",
                      "Amblyomma americanum",
                      "tick surveillance",
                      "county",
                    ],
                    dateModified: g.lastReviewed,
                  }),
                ),
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: () => <NotFoundPage kind="guide" />,
  component: GuideRoute,
});

/**
 * Guides that answer a question the site has real data for open with that data. "Tick
 * season by state" used to tell readers to go and look at 51 state pages, and "Where do
 * Lone Star ticks live?" described the range in words when CDC counts it by county.
 */
function leadFor(slug: string): GuideLead | undefined {
  if (slug === "tick-season-by-state") {
    return { title: "Every state, month by month", node: <SeasonChart states={STATES} /> };
  }
  if (slug === "where-do-lone-star-ticks-live") {
    return { title: "Every state, county by county", node: <RangeTable states={STATES} /> };
  }
  return undefined;
}

function GuideRoute() {
  const { guide } = Route.useLoaderData();
  return <GuidePage guide={guide} lead={leadFor(guide.slug)} />;
}
